/**
 * Shared HH:MM(:SS) parsing helpers, including the `+Nd HH:MM` overnight
 * prefix the departures boards emit. Consolidates what `journey-summary.ts`
 * and `timebar.ts` had drifted between; keep both consumers pointing here so a
 * fix in one always lands in the other.
 */

const MINUTES_PER_DAY = 24 * 60;
const OVERNIGHT_PREFIX_RE = /^\+(\d+)d\s+(\d{1,2}):(\d{2})/;
const HHMM_PREFIX_RE = /^(\d{1,2}):(\d{2})/;

/**
 * Normalizes to zero-padded `HH:MM`; supports `H(H):MM(:SS)` and the overnight
 * `+Nd HH:MM` prefix. Returns '' when no supported prefix matches.
 */
export function toHHMM(time: string): string {
  const overnight = OVERNIGHT_PREFIX_RE.exec(time);
  if (overnight) return `+${overnight[1]}d ${overnight[2].padStart(2, '0')}:${overnight[3]}`;
  const match = HHMM_PREFIX_RE.exec(time);
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : '';
}

/**
 * Minutes since midnight for an `HH:MM(:SS)` or `+Nd HH:MM` string. The `+Nd`
 * prefix adds `N * 1440` minutes so overnight comparisons stay ordered.
 * Returns undefined on malformed input. Hours may exceed 24 within a single day
 * for overnight GTFS times; the returned number preserves that.
 */
export function parseHHMMMinutes(time: string): number | undefined {
  const overnight = OVERNIGHT_PREFIX_RE.exec(time);
  if (overnight) {
    return (
      Number(overnight[1]) * MINUTES_PER_DAY + Number(overnight[2]) * 60 + Number(overnight[3])
    );
  }
  const match = HHMM_PREFIX_RE.exec(time);
  if (!match) return undefined;
  return Number(match[1]) * 60 + Number(match[2]);
}
